<template>
  <div class="email-group">
    <div class="d-flex">
      <table class="table mb-0">
        <thead>
          <tr>
            <th scope="col">
              Group Name
            </th>
            <th scope="col" class="email-group-last-updated-column">
              Last Updated
            </th>
            <th scope="col" class="email-group-actions-column">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="initialRequestInProgress">
            <td colspan="4" class="align-middle">
              <div class="d-flex justify-content-center align-items-center">
                <span>Loading...</span><i class="bx bx-loader-alt bx-spin ms-2" />
              </div>
            </td>
          </tr>
          <tr
            v-for="(emailGroup, idx) in emailGroups"
            v-else-if="emailGroups.length"
            :key="emailGroup.name + idx"
          >
            <td class="align-middle">
              <div>
                <email-group-name-cell
                  :company="company"
                  :email-group="emailGroup"
                  @email-group-updated="onEmailGroupUpdated"
                  @email-group-selected="onEmailGroupSelected"
                />
              </div>
            </td>
            <td class="align-middle email-group-last-updated-column">{{ formatDateTime(emailGroup.updatedAt) }}</td>
            <td class="email-group-actions-column align-middle">
              <GuButton 
                variant="primary"
                size="sm" 
                @click="onEmailGroupSelected(emailGroup)"
              >
                View
              </GuButton>
              <GuButton 
                variant="danger"
                size="sm"
                class="ms-2"
                @click="onDeleteEmailGroup(emailGroup)"
              >
                Delete
              </GuButton>
            </td>
          </tr>
          <tr v-else>
            <td colspan="4" class="align-middle">
              <div class="d-flex justify-content-center align-items-center">
                No email groups found
              </div>
            </td>
          </tr>
          <tr v-if="createNewEmailGroup">
            <td colspan="4" class="border-bottom-0 pt-4 pb-0">
              <div class="d-flex align-items-center">
                <GuInput 
                  v-model="newEmailGroupName" 
                  size="sm"
                  class="new-email-group-name-input" 
                  @keyup.enter="saveNewEmailGroup"
                />
                <i v-if="createRequestInProgress" class="bx bx-loader-alt bx-spin ms-2" />
                <GuButton
                  :disabled="!newEmailGroupName || requestInProgress"
                  variant="primary"
                  class="ms-2"
                  aria-label="Save Email Group"
                  @click="saveNewEmailGroup"
                >
                  Save
                </GuButton>
                <GuButton
                  :disabled="requestInProgress"
                  variant="secondary"
                  class="ms-2"
                  aria-label="Cancel"
                  @click="createNewEmailGroup = false"
                >
                  Cancel
                </GuButton>
              </div>
            </td>
          </tr>
          <tr v-else>
            <td colspan="4" class="border-bottom-0 pt-4 pb-0">
              <GuButton
                :disabled="createNewEmailGroup || requestInProgress"
                variant="primary"
                aria-label="Create Email Group"
                @click="createEmailGroup"
              >
                Create Email Group
              </GuButton>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, ref, defineEmits } from 'vue'

import { GuButton } from '@shared/gu-components/button'
import { GuInput } from '@shared/gu-components/input'

import { 
    createCompanyEmailGroup,
    deleteCompanyEmailGroup,
    getCompanyEmailGroups,
} from '@companies/api/companies.api'
import { useCompanyStore } from '@companies/stores/CompanyStore'

import { useToast } from '@shared/gu-components/toast'

import { formatDate } from '@shared/utils/date'
import { DATE_FORMAT_LONG } from '@templates/constants/dateFormat'

import EmailGroupNameCell from '@companies/components/EmailGroupNameCell.vue'

const companyStore = useCompanyStore()

const { company, getCompanyId } = storeToRefs(companyStore)

const newEmailGroupName = ref('')
const createRequestInProgress = ref(false)
const deleteRequestInProgress = ref(false)

const { toast } = useToast()

const emit = defineEmits(['emailGroupSelected'])

const {
  data: emailGroupsData,
  isPending: initialRequestInProgress,
} = getCompanyEmailGroups(getCompanyId.value, { gcTime: 10000 })

const emailGroups = computed(() => emailGroupsData.value ?? [])

const createNewEmailGroup = ref(false)

function createEmailGroup() {
    createNewEmailGroup.value = true
}

function formatDateTime (datetime) {
    return formatDate(datetime, DATE_FORMAT_LONG)
}

function onEmailGroupSelected (emailGroup) {
  emit('emailGroupSelected', emailGroup)
}

function onEmailGroupUpdated (emailGroup) {
  const idx = emailGroupsData.value.findIndex(emailGroupItem => emailGroupItem.id === emailGroup.id) 
  emailGroupsData.value = emailGroupsData.value.with(idx, emailGroup)
}

const { mutateAsync: createCompanyEmailGroupMutation } = createCompanyEmailGroup()
async function saveNewEmailGroup () {
  if (newEmailGroupName.value) {
    try {
      createRequestInProgress.value = true
      const response = await createCompanyEmailGroupMutation({ 
        companyId: getCompanyId.value,
        companyName: newEmailGroupName.value,
      })
      createRequestInProgress.value = false
      emailGroupsData.value.push({
        id: response.id,
        name: response.name,
        updatedAt: response.updatedAt,
      })
      newEmailGroupName.value = ''
      createNewEmailGroup.value = false
      toast({
        variant: 'success',
        description: `Email group "${response.name}" created successfully`,
      })
    } catch (error) {
      createRequestInProgress.value = false
      console.error('Error occured creating email group:', error)
      toast({
        variant: 'danger',
        description: error?.response?.data?._message || 'Error creating email group, please try again',
      })
    }
  }
}

const { mutateAsync: deleteCompanyEmailGroupMutation } = deleteCompanyEmailGroup()
async function onDeleteEmailGroup (emailGroup) {
  deleteRequestInProgress.value = true
  try {
    const response = await deleteCompanyEmailGroupMutation({
      companyId: getCompanyId,
      companyEmailGroupId: emailGroup.id,
    })
    if (response?.deleted) {
      emailGroupsData.value = emailGroupsData.value.filter(emailGroupItem => emailGroupItem.id !== emailGroup.id)
    }
    toast({
      variant: 'success',
      description: `Email group "${emailGroup.name}" deleted successfully`,
    })
  } catch (error) {
    console.error('Error occured deleting email group:', error)
    toast({
      variant: 'danger',
      description: error?.response?.data?._message || 'Error deleting email group, please try again',
      duration: Infinity,
    })
  }
  deleteRequestInProgress.value = false
}
</script>

<style scoped>
.email-group td {
  height: 48px;
}
.email-group-actions-column {
  width: 142px;
  max-width: 142px;
}
.email-group-last-updated-column {
  width: 175px;
  max-width: 175px;
}
</style>